/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */

'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams, useRouter, usePathname } from 'next/navigation'
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  type SxProps,
} from '@mui/material'
import { useTranslations } from 'next-intl'
import { appPaths, getResourceBasePath } from '@config'
import { RESOURCE_TYPE, getTranslationNamespace } from '@models'
import { Icon } from '@media-components'
import {
  PostCardProps,
  MilestoneCardSkeleton,
  MileStoneCard,
  MilestoneCardData,
} from '@components/common'
import {
  PaginationSearchSection,
  CategoryFilterSection,
  NewsLetter,
  ConsultationSection,
  type ExploreBannerData,
} from '@components/feature/common'
import { DATE_FORMAT, formatCaseStudyDateRange, formatDate } from '@utils/date'
import { SCROLL_ID } from '@utils/scroll/model/scroll.id'
import { CustomStyles, getStyles } from '@utils/styles'
import { useResourcesSearchSiteFetch } from 'src/client-query/search'
import defaultStyles, { StylesClassNames, getNewsletterStyles } from './styles'

const SELECT_SORT_OPTIONS = {
  LATEST: 'latest',
  OLDEST: 'oldest',
} as const

const RESOURCE_SEARCH_QUERY_PARAM = 'search'

function buildResourceListHref(
  pathname: string,
  searchParams: { toString: () => string },
  searchQuery: string
) {
  const params = new URLSearchParams(searchParams.toString())
  const trimmed = searchQuery.trim()

  if (trimmed) {
    params.set(RESOURCE_SEARCH_QUERY_PARAM, trimmed)
  } else {
    params.delete(RESOURCE_SEARCH_QUERY_PARAM)
  }

  const qs = params.toString()
  return qs ? `${pathname}?${qs}` : pathname
}

const milesStoneDataExtractor = (
  cardData: PostCardProps[]
): MilestoneCardData[] => {
  return cardData.map(
    ({
      description,
      imageUrl,
      time,
      title,
      authorProfile,
      date,
      href,
      categories,
      createdAt,
      actionButtonData,
      logoData,
      caseStudyDateRange,
    }) => ({
      title,
      description,
      imageData: {
        src: imageUrl,
        alt: title,
      },
      blogData: {
        authorName: authorProfile.authorName || '',
        timeRead: time,
        date,
        authorImage: authorProfile.authorImage,
        authorSlug: authorProfile.authorSlug,
      },
      href,
      categories,
      createdAt,
      actionButtonData,
      logoData,
      caseStudyDateRange,
    })
  )
}

export type TabProps = {
  label: string
}

/**
 * Common data type for resource list sections
 * Can be used for Blogs, Case Studies, Events, etc.
 */
export type ResourceListSectionData = {
  cards: PostCardProps[]
  categories: { id: string; name: string; slug: string }[]
}

// Alias types for backwards compatibility
export type RecentPostSectionData = ResourceListSectionData
export type RecentCaseStudySectionData = ResourceListSectionData

export type ResourceListSectionProps = ResourceListSectionData & {
  customStyles?: CustomStyles<StylesClassNames>
  wrapperSx?: SxProps
  /** Current selected category (e.g., from URL path) */
  currentCategory?: string
  /** Resource type for search and navigation */
  resourceType?: RESOURCE_TYPE
  showRelatedCategories?: boolean
  showFilterableCategories?: boolean
  sideCtaBannerData?: ExploreBannerData
  showNewsLetterSection?: boolean
}

/**
 * Generic resource list section component
 * Works for Blogs, Case Studies, Events, or any other resource type
 */
export function ResourceListSection({
  customStyles,
  cards,
  wrapperSx,
  categories,
  currentCategory,
  resourceType = RESOURCE_TYPE.Blogs,
  showRelatedCategories = false,
  showFilterableCategories = false,
  sideCtaBannerData,
  showNewsLetterSection = false,
}: ResourceListSectionProps) {
  const searchParams = useSearchParams()
  const searchFromParams = searchParams.get(RESOURCE_SEARCH_QUERY_PARAM) ?? ''
  const currentUrl = usePathname()

  const styles = getStyles(defaultStyles, customStyles)
  const router = useRouter()

  const [blogSearch, setBlogSearch] = useState(searchFromParams)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [currentSort, setCurrentSort] = useState<string>(
    SELECT_SORT_OPTIONS.LATEST
  )
  /**
   * While searching, Typesense order is preserved unless the user picks a sort
   * option from the menu (then we apply date sort on top of search results).
   */
  const [explicitDateSortWhileSearching, setExplicitDateSortWhileSearching] =
    useState(false)

  const isSearchActive = blogSearch.trim().length > 0
  const preserveTypesenseOrder =
    isSearchActive && !explicitDateSortWhileSearching

  const openSortMenu = Boolean(anchorEl)

  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
    document
      .querySelector(SCROLL_ID.RECENT_POSTS)
      ?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleSortClose = () => {
    setAnchorEl(null)
  }

  const handleSortChange = (sort: string) => {
    setCurrentSort(sort)
    if (blogSearch.trim()) {
      setExplicitDateSortWhileSearching(true)
    }
    handleSortClose()
  }

  // Get translation namespace from resource type
  const translationNamespace = getTranslationNamespace(resourceType)
  const tfeatures = useTranslations(translationNamespace)
  const tcommon = useTranslations('NewsLetterSection')

  const filteredCategories = useMemo(() => {
    const tempFilteredCategories = categories.filter((category) => {
      if (category.slug !== currentCategory) return true
      return false
    })
    return tempFilteredCategories
  }, [currentCategory, categories])

  const sortButtonLabel = (() => {
    if (preserveTypesenseOrder) {
      return tfeatures('sort.label')
    }
    if (currentSort === SELECT_SORT_OPTIONS.OLDEST) {
      return tfeatures('sort.oldest')
    }
    return tfeatures('sort.latest')
  })()

  const SortButton = useMemo(() => {
    return (
      <>
        <Button
          onClick={handleSortClick}
          variant="outlined"
          sx={{
            '& .MuiButton-startIcon': {
              marginRight: '0px !important',
            },
          }}
          startIcon={
            <Icon
              iconName="Sort"
              sx={{
                fontSize: '1.5rem !important',
                marginTop: '6px',
              }}
            />
          }
        >
          <Typography variant="body2.500">{sortButtonLabel}</Typography>
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={openSortMenu}
          onClose={handleSortClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: '8px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
              minWidth: '180px',
            },
          }}
        >
          <MenuItem
            onClick={() => handleSortChange(SELECT_SORT_OPTIONS.LATEST)}
            selected={
              !preserveTypesenseOrder &&
              currentSort === SELECT_SORT_OPTIONS.LATEST
            }
          >
            <Typography variant="body2.400">
              {tfeatures('sort.latestToOldest')}
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={() => handleSortChange(SELECT_SORT_OPTIONS.OLDEST)}
            selected={
              !preserveTypesenseOrder &&
              currentSort === SELECT_SORT_OPTIONS.OLDEST
            }
          >
            <Typography variant="body2.400">
              {tfeatures('sort.oldestToLatest')}
            </Typography>
          </MenuItem>
        </Menu>
      </>
    )
  }, [
    anchorEl,
    openSortMenu,
    currentSort,
    preserveTypesenseOrder,
    sortButtonLabel,
    tfeatures,
  ])

  // Convert cards to milestone data format
  const allCardData = useMemo(
    () => milesStoneDataExtractor(cards) ?? [],
    [cards]
  )

  // Extract all unique category names from all cards into a flat array
  const categoryData = useMemo(() => {
    const allCategories = cards.flatMap(({ categories }) => categories || [])
    const unique = new Map()
    allCategories.forEach((cat) => unique.set(cat.slug, cat))
    return Array.from(unique.values())
  }, [cards])

  // API-based search with resource type
  const {
    search: onSearch,
    data: searchResult,
    isLoading,
  } = useResourcesSearchSiteFetch({
    enabled: true,
    count: 10,
    resourceType,
    searchValue: searchFromParams.trim(),
    adapterFn(data) {
      const res: MilestoneCardData = {
        description: data.shortDescription,
        href: `${getResourceBasePath(resourceType)}/${data.slug}`,
        imageData: {
          alt: data.imageAltTag,
          src: data.imageUrl,
        },
        title: data.title,
        blogData: {
          authorName: data.authorName,
          timeRead: data.readingTime,
          date: formatDate(data.createdAt, DATE_FORMAT.Short),
          authorImage: data.authorImageUrl
            ? {
                alt: data.authorImageAltTag,
                src: data.authorImageUrl,
              }
            : undefined,
          authorSlug: data.authorSlug,
        },
        categories: data.categories?.map((item) => {
          // Typesense stores category names; resolve to CMS slug for category URLs
          const matched = categories.find(
            (c) =>
              c.name === item ||
              c.slug === item ||
              c.name.toLowerCase() === item.toLowerCase()
          )
          return {
            name: matched?.name ?? item,
            slug: matched?.slug ?? item,
            id: matched?.id ?? item,
          }
        }),
        createdAt: data.createdAt,
        actionButtonData: data.actionButtonData,
        logoData: data.logoUrl
          ? {
              alt: data.logoAltTag || data.title,
              src: data.logoUrl,
            }
          : data.logoData,
        caseStudyDateRange: formatCaseStudyDateRange(
          data.caseStudyStartDate,
          data.caseStudyEndDate
        ),
      } as MilestoneCardData
      return res
    },
  })

  // Keep the input and Typesense query in sync with `?search=` (refresh, share, back/forward)
  useEffect(() => {
    if (
      searchFromParams === blogSearch ||
      searchFromParams === blogSearch.trim()
    ) {
      return
    }

    setBlogSearch(searchFromParams)
    const trimmed = searchFromParams.trim()
    if (trimmed) {
      setExplicitDateSortWhileSearching(false)
      onSearch(trimmed)
      return
    }

    setCurrentSort(SELECT_SORT_OPTIONS.LATEST)
    setExplicitDateSortWhileSearching(false)
  }, [searchFromParams])

  const handleSearch = useCallback(
    (text: string) => {
      setBlogSearch(text)
      const nextHref = buildResourceListHref(currentUrl, searchParams, text)
      const currentQuery = searchParams.toString()
      const currentHref = currentQuery
        ? `${currentUrl}?${currentQuery}`
        : currentUrl

      if (nextHref !== currentHref) {
        router.replace(nextHref, { scroll: false })
      }

      const trimmed = text.trim()
      if (trimmed) {
        setExplicitDateSortWhileSearching(false)
        onSearch(trimmed)
      } else {
        setCurrentSort(SELECT_SORT_OPTIONS.LATEST)
        setExplicitDateSortWhileSearching(false)
      }
    },
    [onSearch, router, currentUrl, searchParams]
  )

  // Use search results only when there is a non-whitespace query (spaces alone = browse mode)
  const baseData = useMemo(
    () => (isSearchActive ? searchResult : allCardData) || [],
    [isSearchActive, searchResult, allCardData]
  )

  // Sort by date when browsing or when user explicitly sorts during search;
  // otherwise keep Typesense relevance order for search hits.
  const data = useMemo(() => {
    if (!baseData.length) return baseData
    if (preserveTypesenseOrder) return baseData

    return [...baseData].sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0

      if (currentSort === SELECT_SORT_OPTIONS.OLDEST) {
        return dateA - dateB
      }
      return dateB - dateA
    })
  }, [baseData, currentSort, preserveTypesenseOrder])

  // Newsletter component for the aside section
  const NewsletterSection = useMemo(() => {
    return (
      <NewsLetter
        size="small"
        heading={tcommon('blogNewsLetterSection.heading')}
        description={tcommon('blogNewsLetterSection.description')}
        newsLetterPageCategory="BLOG"
        sectionWrapperProps={{ noWrapper: true }}
      />
    )
  }, [])

  const ConsultationSectionCard = useMemo(() => {
    if (!sideCtaBannerData) {
      return null
    }

    return (
      <Box
        sx={{
          overflow: 'hidden',
          borderRadius: '16px',
        }}
      >
        <ConsultationSection
          {...sideCtaBannerData}
          sectionWrapperProps={{ noWrapper: true }}
          csx={getNewsletterStyles()}
          showTextField={false}
        />
      </Box>
    )
  }, [sideCtaBannerData])

  // Category filter section for the aside

  const disableAllOptions = useMemo(() => {
    return currentUrl.includes(appPaths.category)
  }, [currentUrl])

  const CategorySection = useMemo(() => {
    return (
      <CategoryFilterSection
        categoryData={categories}
        categoryTitle={tfeatures('accordionTitle')}
        categoryLinkBasePath={`${getResourceBasePath(resourceType)}${appPaths.category}`}
        customStyles={customStyles}
        // Check if the category matches the URL category
        isFilterSelected={(categorySlug) => currentCategory === categorySlug}
        withAllOption={!disableAllOptions}
      />
    )
  }, [
    categoryData,
    tfeatures('accordionTitle'),
    customStyles,
    currentCategory,
    categories,
    resourceType,
    disableAllOptions,
  ])

  const RelatedCategorySection = useMemo(() => {
    return (
      <CategoryFilterSection
        categoryData={filteredCategories}
        categoryTitle={tfeatures('relatedCategory')}
        categoryLinkBasePath={`${getResourceBasePath(resourceType)}${appPaths.category}`}
        customStyles={customStyles}
        // Check if the category matches the URL category
        isFilterSelected={(categorySlug) => currentCategory === categorySlug}
        withAllOption={!disableAllOptions}
      />
    )
  }, [
    categoryData,
    tfeatures('relatedCategory'),
    customStyles,
    currentCategory,
    filteredCategories,
    resourceType,
    disableAllOptions,
  ])

  // Loading skeleton component
  const LoadingSkeleton = useMemo(() => {
    return (
      <MilestoneCardSkeleton
        csx={{
          content: styles('milestoneCardContent').sx,
          leftView: styles('leftView').sx,
          image: styles('image').sx,
        }}
      />
    )
  }, [styles])

  const asideSectionsList = useMemo(() => {
    const asideSectionsList: React.ReactNode[] = []
    if (showFilterableCategories) {
      asideSectionsList.push(CategorySection)
    }
    if (showRelatedCategories) {
      asideSectionsList.push(RelatedCategorySection)
    }
    if (showNewsLetterSection) {
      asideSectionsList.push(NewsletterSection)
    }
    if (sideCtaBannerData) {
      asideSectionsList.push(ConsultationSectionCard)
    }

    return asideSectionsList
  }, [
    showFilterableCategories,
    showNewsLetterSection,
    sideCtaBannerData,
    showRelatedCategories,
    CategorySection,
    NewsletterSection,
    ConsultationSectionCard,
    RelatedCategorySection,
  ])

  return (
    <PaginationSearchSection<MilestoneCardData>
      title={tfeatures('heading')}
      searchPlaceholder={tfeatures('searchPlaceholder')}
      searchValue={blogSearch}
      onSearch={handleSearch}
      data={data}
      // Pagination - just pass initialItemsPerPage, component handles the rest!
      initialItemsPerPage={10}
      // Loading states
      loading={isSearchActive ? isLoading : false}
      loadingSkeleton={LoadingSkeleton}
      skeletonCount={10}
      // Empty states
      notFoundTitle={tfeatures('notFoundTitle')}
      notFoundDescription={tfeatures('notFoundDescription')}
      clearSearchLabel={tfeatures('clearSearchLabel')}
      // Aside sections - order matters!
      asideSections={asideSectionsList}
      mobileAsideSections={asideSectionsList}
      // Header Actions
      headerActions={SortButton}
      // Styling & misc
      scrollId={SCROLL_ID.RECENT_POSTS}
      wrapperSx={wrapperSx}
      customStyles={customStyles}
      // Mobile category tag - shown when on a category page
      activeCategoryTag={
        categories?.find((c) => c.slug === currentCategory)?.name ||
        currentCategory
      }
      activeCategoryLabel={tfeatures('accordionTitle')}
      onClearCategory={() => {
        router.push(getResourceBasePath(resourceType))
      }}
      // Render each item
      renderItem={(cardData, idx) => (
        <MileStoneCard
          key={`${cardData.title}-${idx}`}
          milestoneCardData={cardData}
          onCategoryClick={(categorySlug) => {
            router.push(
              `${getResourceBasePath(resourceType)}${appPaths.category}/${categorySlug}`
            )
          }}
          csx={{
            content: styles('milestoneCardContent').sx,
            title: styles('mileStoneTitle').sx,
            description: styles('mileStoneDescription').sx,
            leftView: styles('leftView', {
              ...(resourceType === RESOURCE_TYPE.CaseStudies && {
                justifyContent: 'unset',
                alignSelf: 'center',
              }),
            }).sx,
            image: styles('image').sx,
            blogData: styles('blogData').sx,
          }}
          resourceType={resourceType}
        />
      )}
    />
  )
}

// Alias components for backwards compatibility
export const RecentPostSection = ResourceListSection
export const RecentCaseStudySection = ResourceListSection
